/*=========================================================================================
  File Name: store.js
  Description: Vuex store
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Vuex from "vuex";

import state from "./state";
import getters from "./getters";
import mutations from "./mutations";
import actions from "./actions";

import createPersistedState from "vuex-persistedstate";

// 共通系
import auth from "./auth/moduleAuth";
import notification from "./notification/moduleNotification";
import session from "./modules/session";
import account from "./modules/account";
import constantResource from "./modules/moduleConstantResource";

// 法人系
import mapSetting from "./enterprise/map/setting/moduleEnterpriseMapSetting";
import liveMap from "./enterprise/live-map/moduleLiveMap";
import enterpriseUser from "./enterprise/user/moduleUser";
import enterpriseUserList from "./enterprise/user-list/moduleUserList";
import enterpriseUserView from "./enterprise/user-view/moduleUserView";
import enterpriseUserAdd from "./enterprise/user-add/moduleUserAdd";
import enterpriseUserEdit from "./enterprise/user-edit/moduleUserEdit";
import enterpriseLicenseEdit from "./enterprise/license-edit/moduleLicenseEdit";
import enterpriseLicenseAdd from "./enterprise/license-add/moduleLicenseAdd";
import enterpriseVessel from "./enterprise/vessel/moduleEnterpriseVessel";
import enterpriseVesselList from "./enterprise/vessel-list/moduleEnterpriseVesselList";
import enterpriseVesselView from "./enterprise/vessel-view/moduleEnterpriseVesselView";
import enterpriseVesselEdit from "./enterprise/vessel-edit/moduleEnterpriseVesselEdit";
import enterpriseCameraList from "./enterprise/camera-list/moduleCameraList";
import enterpriseMapVessel from "./enterprise/map/vessel/moduleEnterpriseMapVessel";
import enterpriseMapVesselSelected from "./enterprise/map/vessel_selected/moduleEnterpriseMapVesselSelected";
import enterpriseMapVesselTarget from "./enterprise/map/vessel_target/moduleEnterpriseMapVesselTarget";
import enterpriseMapVesselSearch from "./enterprise/map/vessel_search/moduleEnterpriseMapVesselSearch";
import enterprisePlotEdit from "./enterprise/plot/edit/modulePlotEdit";
import enterprisePlot from "./enterprise/plot/modulePlot";
import enterprisePlotTarget from "./enterprise/plot/plot_target/moduleMapPlotTarget";
import enterprisePlotList from "./enterprise/plot/list/modulePlotList";
import enterpriseMapSetting from "./enterprise/map/setting/moduleEnterpriseMapSetting";
import enterpriseVoyageHistory from "./enterprise/voyage-history/moduleVoyageHistory";
import radarGridPlayback from "./enterprise/radar-grid/playback/moduleRGPlayback";
import enterpriseVesselVoyageHistory from "./enterprise/vessel-voyage-history/moduleVoyageHistory";
import enterpriseVesselMonitoringEngine from "./enterprise/vessel-monitoring-engine/moduleVesselMonitoringEngine";
import enterpriseDocumentManager from "./enterprise/document-manager/moduleEnterpriseDocumentManager";
import forecastWindy from "./enterprise/live-map/forecast-windy/moduleForecastWindy";
import enterpriseBackgroundTaskManager from "./enterprise/background-task-manager/moduleBackgroundTaskManager";
import enterpriseWorkflows from "./enterprise/workflows";
import enterpriseForms from "./enterprise/forms";

// 船員管理
import crewAccount from "./enterprise/crew-account/moduleCrewAccount";
import crewManningVessels from "./enterprise/crew-manning-vessels/moduleManningVessels";
import crewLaborTemplate from "./enterprise/crew-labor-template/moduleCrewLaborTemplate";
import crewList from "./enterprise/crew-list/moduleCrewList";
import manningVesselList from "./enterprise/manning-vessel-list/moduleManningVesselList";
import crewLabor from "./enterprise/crew-labor/moduleCrewLabor";
import crewTier from "./enterprise/crew-tier/moduleCrewTier";
import crewTimesheet from "./enterprise/crew-timesheet/moduleCrewTimesheet";
import crewDataManagement from "./enterprise/crew-data-management/moduleCrewDataManagement";

// 機能拡張系
import vesselReportList from "./addon/vessel-report-list/moduleVesselReportList";
import vesselReportView from "./addon/vessel-report-view/moduleVesselReportView";
import vesselReportExport from "./addon/vessel-report-export/moduleVesselReportExport";
import portMasterDestinationWork from "./addon/port-master/destination-work/moduleDestinationWork";
import portMasterDestinationWorkEdit from "./addon/port-master/destination-work-edit/moduleDestinationWorkEdit";
import vesselNoonReportList from "./addon/vessel-noon-report-list/moduleVesselNoonReportList";
import vesselNoonReportDetail from "./addon/vessel-noon-report-detail/moduleVesselNoonReportDetail";

// operation
import operations from "./addon/operation/operations";
import operationReports from "./addon/operation/operationReports";
import operationWorkflows from "./addon/operation/operationWorkflows";
import operationColumns from "./addon/operation/operationColumns";
import operationAccounts from "./addon/operation/operationAccounts";
import operationVessels from "./addon/operation/operationVessels";
import operationReport from "./addon/operation/operationReport";

// 損害対応
import hullInsurance from "./addon/hull-insurance/moduleHullInsurance";
import hullInsuranceAddReport from "./addon/hull-insurance-add-report/moduleHullInsuranceAddReport";
import hullInsuranceReportView from "./addon/hull-insurance-report-view/moduleHullInsuranceReportView";

// 省エネ解析
import fuelConsumption from "./addon/fuel-consumption/moduleFuelConsumption";

// secure-dataflow系
import sdfCommons from "./addon/secure-dataflow/commons/moduleCommons";
import sdfMenu from "./addon/secure-dataflow/menu/moduleMenu";
import monitoringVesselManeuvering from "./addon/secure-dataflow/monitoring-vessel-maneuvering/moduleMonitoringVesselManeuvering";
import monitoringVesselEngine from "./addon/secure-dataflow/monitoring-vessel-engine/moduleMonitoringVesselEngine";
import checklistTrendDataReport from "./addon/secure-dataflow/check-list/trend-data-report/moduleTrendDataReport";
import m0CheckListReport from "./addon/secure-dataflow/check-list/check-list-report/moduleM0CheckListReport";

import portMaster from "./addon/port-master/modulePortMaster";
import ecoShip from "./addon/eco-ship/moduleEcoShip";

import ecoShipVoyagePlan from "./addon/eco-ship/voyage-plan/moduleVoyagePlan";

Vue.use(Vuex);

const store = new Vuex.Store({
  getters,
  mutations,
  state,
  actions,
  strict: process.env.NODE_ENV !== "production",
  plugins: [
    createPersistedState({
      paths: [
        "portMaster.selectedPort",
        "portMasterDestinationWorkEdit.destinationWorkImportedTasksMap",
        "enterpriseVesselView.kindOriginVersionMap",
        "portMaster.storedMasterSearchCondition",
        "ecoShipVoyagePlan.seaRouteDetailCondition",
        "ecoShip.enterpriseVesselsWithContainers",
        "portMasterDestinationWorkEdit.selectedDateDisplay1",
        "ecoShip.voyageHistoryDetail",
      ],
    }),
  ],
  modules: {
    auth: auth,
    mapSetting: mapSetting,
    liveMap: liveMap,
    notification: notification,
    enterpriseUserList: enterpriseUserList,
    enterpriseUserView: enterpriseUserView,
    enterpriseUserAdd: enterpriseUserAdd,
    enterpriseUserEdit: enterpriseUserEdit,
    enterpriseLicenseEdit: enterpriseLicenseEdit,
    enterpriseLicenseAdd: enterpriseLicenseAdd,
    enterpriseVesselList: enterpriseVesselList,
    enterpriseVesselView: enterpriseVesselView,
    enterpriseVesselEdit: enterpriseVesselEdit,
    enterpriseCameraList: enterpriseCameraList,
    session: session,
    account: account,
    vesselReportList: vesselReportList,
    vesselReportView: vesselReportView,
    vesselReportExport: vesselReportExport,
    vesselNoonReportList: vesselNoonReportList,
    vesselNoonReportDetail: vesselNoonReportDetail,
    enterpriseDocumentManager: enterpriseDocumentManager,
    enterpriseUser,
    enterpriseVessel,
    enterpriseMapVessel,
    enterpriseMapVesselSelected,
    enterpriseMapVesselTarget,
    enterpriseMapVesselSearch,
    enterprisePlot,
    enterprisePlotList,
    enterprisePlotEdit,
    enterprisePlotTarget,
    enterpriseMapSetting,
    enterpriseVesselMonitoringEngine,
    constantResource,
    enterpriseVoyageHistory,
    enterpriseVesselVoyageHistory,
    forecastWindy: forecastWindy,
    enterpriseBackgroundTaskManager: enterpriseBackgroundTaskManager,
    enterpriseWorkflows,
    enterpriseForms,

    radarGridPlayback,

    // 船員管理
    crewAccount,
    crewManningVessels,
    crewLaborTemplate,
    crewList,
    manningVesselList,
    crewLabor,
    crewTier,
    crewTimesheet,
    crewDataManagement,

    //損害対応
    hullInsurance,
    hullInsuranceAddReport,
    hullInsuranceReportView,

    //アブログ
    fuelConsumption,

    // operation
    operations,
    operationReports,
    operationWorkflows,
    operationColumns,
    operationAccounts,
    operationVessels,
    operationReport,

    // secure-dataflow系
    sdfMenu: sdfMenu,
    sdfCommons: sdfCommons,
    sdfMonitoringVesselManeuvering: monitoringVesselManeuvering,
    monitoringVesselEngine: monitoringVesselEngine,
    checklistTrendDataReport: checklistTrendDataReport,
    m0CheckListReport: m0CheckListReport,
    portMaster,
    portMasterDestinationWork,
    portMasterDestinationWorkEdit,
    ecoShipVoyagePlan,
    ecoShip,
  },
});

export default store;
